import React from 'react'
import ReactDOM from 'react-dom/client'
import App from './App.tsx'
import { Client_State_Name, Player_Machine_Snapshot, Websocket_Event_Names } from '@quiz-buzz/client-common'
import toast from 'react-hot-toast'
import { Key_For_Host_XState_Persistence, Key_For_Player_XState_Persistence } from './constants.ts'
import { determine_and_set_environment_for_web } from '../Utilities/Web_Environment_Setup.ts'
import { createActor } from 'xstate'
import { Client_Event_Name } from '@quiz-buzz/common'
import { init_pinch_zoom } from '../Utilities/Pinch_Zoom.ts'
import { host_state_machine, Host_State_Name, set_host_machine_helpers } from '@quiz-buzz/client-common/src/State/Host_State_Machine.ts'
import { player_state_machine, set_player_machine_helpers } from '@quiz-buzz/client-common/src/State/Player_State_Machine.ts'

const player_storage_snapshot_string = sessionStorage.getItem(
	Key_For_Player_XState_Persistence,
)
const player_storage_snapshot = player_storage_snapshot_string
	? JSON.parse(player_storage_snapshot_string)
	: undefined


const host_storage_snapshot_string = sessionStorage.getItem(
	Key_For_Host_XState_Persistence,
)
const host_storage_snapshot = host_storage_snapshot_string
	? JSON.parse(host_storage_snapshot_string)
	: undefined

const toast_impl = {
	info(message: string) {
		toast(message)
	},
	error(message: string) {
		console.log(message)
		toast.error(message)
	},
}

// Chrome and Safari both don't process a websocket onclose event when reloading a web page,
// so this code manually sets the state to connection_closed when starting up a page with
// persisted state.
//
// In firefox it goes user clicks reload -> websocket close -> websocket close handler -> set state to connection_closed -> reload
// In chrome and safari it goes user clicks reload -> websocket close -> reload
// 
// No other js executes so this nasty little conditional below sets the state to the right thing for these browsers.
if (player_storage_snapshot && player_storage_snapshot.value !== Client_State_Name.splash && player_storage_snapshot.value !== Client_State_Name.connection_closed) {
	player_storage_snapshot.value = Client_State_Name.connection_closed
}

determine_and_set_environment_for_web()

const buzzer_test_callback = (name: string) => {
	// Bypass the react render cycle for maximum instant user responsiveness
	const player_item = document.querySelector(
		`[data-player-item="${name}"]`,
	)
	if (player_item) {
		player_item.classList.add('buzzed')
		setTimeout(() => player_item.classList.remove('buzzed'), 500)
	}
}

const clear_game_link = () => {
	window.location.hash = ''
}

set_player_machine_helpers( { clear_game_link, toast: toast_impl, buzzer_test_callback } )

set_host_machine_helpers( { clear_game_link, toast: toast_impl, buzzer_test_callback } )


export const player_machine_actor = createActor(player_state_machine, { 
	snapshot: player_storage_snapshot, 
	inspect: (event) => {
		if (event.type === '@xstate.event') {
			console.log('player machine event: ', event.event)
		}
		if (event.type === '@xstate.snapshot') {
			console.log('new player machine state: ', (event.snapshot as Player_Machine_Snapshot).value)
		}
	} 
})
player_machine_actor.start()

// If we're reloading state from a snapshot, tell the state machine that it was disconnected:
if (player_storage_snapshot && player_machine_actor.getSnapshot().value !== Client_State_Name.connection_closed) { 
	console.log('reloading player state from snapshot - tell state machine that it was disconnected')
	player_machine_actor.send({ type: Websocket_Event_Names.websocket_closed })
}


export const host_machine_actor = createActor(host_state_machine, { 
	snapshot: host_storage_snapshot,
	inspect: (event) => {
		if (event.type === '@xstate.event') {
			console.log('host machine event: ', event.event)
		}
		if (event.type === '@xstate.snapshot') {
			console.log('new host machine state: ', (event.snapshot as Player_Machine_Snapshot).value)
		}
	}
})
host_machine_actor.start()

// If we're reloading state from a snapshot, tell the state machine that it was disconnected:
if (host_storage_snapshot && host_machine_actor.getSnapshot().value !== Host_State_Name.connection_closed) { 
	console.log('reloading host state from snapshot - tell state machine that it was disconnected')
	host_machine_actor.send({ type: Websocket_Event_Names.websocket_closed })
}


function handle_hash_change() {
	if (player_machine_actor.getSnapshot().value === 'splash' && window.location.hash.length === 5) {
		player_machine_actor.send({
			type: 'start_player_registration',
			detail: {source: 'url-hash-change-handler', game_code: window.location.hash.substring(1)},
		})
	}
}

window.addEventListener('hashchange', handle_hash_change)

// We call this here so that if the app has been loaded with a game_code in the URL then
// the app will go straight to player registration for that game_code.
handle_hash_change()

history.pushState({state: 'application started'}, 'Quiz Buzz', '')

// Back button presses should be handled by the state machine, unless it's in connection_closed state
// TODO: work out why there's a special case for connection_closed.
window.addEventListener('popstate', () => {
	if (player_machine_actor.getSnapshot().value !== Client_State_Name.connection_closed) {
		player_machine_actor.send({type: Client_Event_Name.navigate_back})
	}
	if (host_machine_actor.getSnapshot().value !== Host_State_Name.connection_closed) {
		host_machine_actor.send({type: Client_Event_Name.navigate_back})
	}
})

// Set up player session persistence, store the state machine snapshot whenever it changes:
player_machine_actor.subscribe(snapshot => {
	if (snapshot.value === Client_State_Name.splash) {
		sessionStorage.removeItem(
			Key_For_Player_XState_Persistence
		)
	}
	else {
		sessionStorage.setItem(
			Key_For_Player_XState_Persistence,
			JSON.stringify(player_machine_actor.getPersistedSnapshot()),
		)
	}
})

// Set up player session persistence, store the state machine snapshot whenever it changes:
host_machine_actor.subscribe(snapshot => {
	if (snapshot.value === Host_State_Name.splash) {
		sessionStorage.removeItem(
			Key_For_Host_XState_Persistence
		)
	}
	else {
		sessionStorage.setItem(
			Key_For_Host_XState_Persistence,
			JSON.stringify(host_machine_actor.getPersistedSnapshot()),
		)
	}
})


init_pinch_zoom()

ReactDOM.createRoot(document.getElementById('root')!).render(
	<React.StrictMode>
		<App />
	</React.StrictMode>,
)
